import { AlertTriangle, Paperclip } from "lucide-react";
import {
	complexMissingLoadReference,
	getDeclaredComplexReference,
	hasPercentageLikeLoad,
} from "../../lib/programBuilderUtils";

/**
 * Which max the percentages of a complex are based on.
 *
 * On a single exercise a percentage is obviously of that exercise's max. A
 * complex has no max of its own, so the coach has to declare the movement:
 * left implicit, the athlete saw a bare "@50%" and, once the session was
 * logged, the percentages were computed on the result just entered. Here the
 * choice is one click on a movement of the sequence, and an undeclared
 * reference is shown as something to do, never as a default.
 *
 * The reference applies to every row of the complex (`onSelect` sets them
 * all), the same as the paperclip of a standard exercise.
 */
export function ComplexLoadReference({
	exercise,
	rows,
	onSelect,
	onPickOther,
	disabled = false,
	t,
}) {
	const setRows = rows || exercise?.set_rows || [];
	const percentageRows = setRows.filter((row) =>
		hasPercentageLikeLoad(row?.load_percentage),
	);
	if (percentageRows.length === 0) return null;

	const missing = complexMissingLoadReference(exercise, setRows);
	const declared = getDeclaredComplexReference(exercise, percentageRows[0]);
	const movements = (exercise?.complex_exercises || []).filter(
		(item) => item?.exercise_id || item?.exercise_name,
	);
	const isSelected = (item) =>
		Boolean(declared) &&
		!missing &&
		((declared.id && declared.id === item.exercise_id) ||
			(!declared.id &&
				String(declared.name || "").trim().toLowerCase() ===
					String(item.exercise_name || "").trim().toLowerCase()));
	const declaredOutsideSequence =
		declared && !missing && !movements.some(isSelected) ? declared : null;

	const chipClass = (selected) =>
		`h-8 md:h-7 px-2.5 rounded-sm border text-xs md:text-[11px] transition-colors ${
			selected
				? "border-emerald-400/60 bg-emerald-500/10 text-emerald-300"
				: "border-border text-muted-foreground hover:border-emerald-400/50 hover:text-emerald-300"
		} disabled:opacity-50 disabled:pointer-events-none`;

	return (
		<div
			className={`rounded-sm border px-2.5 py-2 space-y-1.5 ${
				missing
					? "border-amber-500/40 bg-amber-500/5"
					: "border-border/60 bg-background/40"
			}`}
		>
			<p
				className={`flex items-center gap-1.5 text-[11px] md:text-[10px] uppercase tracking-wider ${
					missing ? "text-amber-300" : "text-muted-foreground"
				}`}
			>
				{missing ? (
					<AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
				) : (
					<Paperclip className="w-3.5 h-3.5 flex-shrink-0" />
				)}
				<span>
					{missing
						? t("complexLoadReferenceMissing")
						: t("complexLoadReferenceLabel")}
				</span>
			</p>
			<div className="flex flex-wrap gap-1.5">
				{movements.map((item, idx) => (
					<button
						key={`${item.exercise_id || item.exercise_name}_${idx}`}
						type="button"
						disabled={disabled}
						onClick={() =>
							onSelect(item.exercise_id || null, item.exercise_name || null)
						}
						className={chipClass(isSelected(item))}
					>
						{item.exercise_name}
					</button>
				))}
				{declaredOutsideSequence && (
					<span className={chipClass(true)} aria-current="true">
						<span className="inline-flex h-full items-center">
							{declaredOutsideSequence.name ||
								t("loadReferenceExercise")}
						</span>
					</span>
				)}
				{onPickOther && (
					<button
						type="button"
						disabled={disabled}
						onClick={onPickOther}
						className={`${chipClass(false)} border-dashed`}
					>
						{t("complexLoadReferenceOther")}
					</button>
				)}
			</div>
		</div>
	);
}
